import { useEffect, useState } from 'react'
import { Camera, Check, CircleAlert, Crop, RotateCw, ScanText } from 'lucide-react'
import type { LanguagePack } from '../i18n/packs'
import { cropCanvas, rotateCanvas, type CropRect, type QualityReport } from '../services/ocr'
import type { Language } from '../types/document'
import { CropBox } from './CropBox'
import { LanguagePicker } from './LanguagePicker'

const FULL: CropRect = { x: 0, y: 0, w: 1, h: 1 }

/** "Is the photo OK?" — rotate, crop, pick the document's language, then read. */
export function Preview({
  p,
  image,
  quality,
  docLanguage,
  onDocLanguage,
  onRead,
  onRetake
}: {
  p: LanguagePack
  image: HTMLCanvasElement
  quality: QualityReport
  docLanguage: Language
  onDocLanguage: (language: Language) => void
  onRead: (image: HTMLCanvasElement) => void
  onRetake: () => void
}) {
  const [current, setCurrent] = useState(image)
  const [cropping, setCropping] = useState(false)
  const [rect, setRect] = useState<CropRect>(FULL)
  const [url, setUrl] = useState('')

  // Show the photo via a short-lived in-memory object URL (revoked on change/unmount)
  useEffect(() => {
    let objectUrl = ''
    let cancelled = false
    current.toBlob((blob) => {
      if (!blob || cancelled) return
      objectUrl = URL.createObjectURL(blob)
      setUrl(objectUrl)
    }, 'image/jpeg', 0.85)
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [current])

  function finishCrop() {
    const isFull = rect.x === 0 && rect.y === 0 && rect.w === 1 && rect.h === 1
    if (!isFull) setCurrent(cropCanvas(current, rect))
    setRect(FULL)
    setCropping(false)
  }

  const warnings = [quality.dark && p.warnDark, quality.blurry && p.warnBlurry, quality.small && p.warnSmall].filter(Boolean) as string[]

  return (
    <section className="preview-view">
      <h1 className="screen-title">{p.previewTitle}</h1>

      <div className="preview-frame">
        {url && <img src={url} alt="" draggable={false} />}
        {cropping && <CropBox rect={rect} onChange={setRect} />}
      </div>

      <div className="preview-tools">
        <button onClick={() => setCurrent(rotateCanvas(current, 1))} disabled={cropping}>
          <RotateCw size={20} aria-hidden="true" /> {p.rotate}
        </button>
        {cropping ? (
          <button className="active" onClick={finishCrop}>
            <Check size={20} aria-hidden="true" /> {p.cropDone}
          </button>
        ) : (
          <button onClick={() => setCropping(true)}>
            <Crop size={20} aria-hidden="true" /> {p.crop}
          </button>
        )}
        <button onClick={onRetake}>
          <Camera size={20} aria-hidden="true" /> {p.retake}
        </button>
      </div>

      {warnings.map((warning) => (
        <div className="notice-box" key={warning}>
          <CircleAlert size={20} aria-hidden="true" />
          <span>{warning}</span>
        </div>
      ))}

      <LanguagePicker id="doc-language" compact label={p.docLanguageLabel} value={docLanguage} onChange={onDocLanguage} />
      <p className="field-hint">{p.docLanguageHint}</p>

      <button className="primary-button big-action" onClick={() => onRead(current)} disabled={cropping}>
        <ScanText size={24} aria-hidden="true" /> {p.readNow}
      </button>
    </section>
  )
}
